/** The lifecycle banner under an operator page's header, on every tab (tenant retirement): a tenant
 *  that is not active says so at once — "Closed since …; data is read-only for ops", "Code
 *  released; was acme", a release or a restore running. Drawn from the operator record itself
 *  (`lifecycle`, `closed_at`, `released_code`), so it asks for nothing; it links to the Lifecycle
 *  tab, where the state is explained and changed — except on that tab itself. */
import { Alert, AlertTitle, Button } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';

import type { TourOperatorDetail } from '@/api/tourOperators';
import { When } from '@/features/tour-operators/sandbox/When';

import { LIFECYCLE_LABEL, isClosedLifecycle } from './lifecycleWords';

export function LifecycleBanner({
  to,
  onLifecycleTab,
}: {
  to: Pick<TourOperatorDetail, 'id' | 'lifecycle' | 'closed_at' | 'released_code'>;
  onLifecycleTab: boolean;
}) {
  const lifecycle = to.lifecycle;
  if (!lifecycle || !isClosedLifecycle(lifecycle)) return null;
  const tabLink = onLifecycleTab ? undefined : (
    <Button
      component={RouterLink}
      to={`/tour-operators/${to.id}/lifecycle`}
      color="inherit"
      size="small"
    >
      Lifecycle tab
    </Button>
  );
  const was = to.released_code ? `; was ${to.released_code}` : '';
  return (
    <Alert severity="warning" action={tabLink} sx={{ mb: 2 }} data-lifecycle-banner={lifecycle}>
      <AlertTitle sx={{ mb: 0.25 }}>{LIFECYCLE_LABEL[lifecycle]}</AlertTitle>
      {lifecycle === 'closed' && (
        <>
          Closed since <When iso={to.closed_at} />; data is read-only for ops. Every surface refuses
          this tenant — its users, API keys, webhooks, sends and tasks.
        </>
      )}
      {lifecycle === 'releasing' && <>A job is releasing the code{was}. The tenant stays closed.</>}
      {lifecycle === 'released' && <>Code released{was}. The tenant stays closed.</>}
      {lifecycle === 'restoring' && <>A job is restoring the code{was}. The tenant stays closed until it has finished.</>}
    </Alert>
  );
}
